import type { IdentityProviderInfo } from './auth/providers';
import { useMessages } from './i18n/context';

/**
 * The signed-out screen: one button per identity provider the tilgong auth server advertises
 * (ADR-024 launch posture — Google always; more as the platform enables them). Pure —
 * provider fetching and the PKCE redirect live in the shell/auth layer.
 */
export function SignIn({
  providers,
  notice = null,
  install = null,
  onSignIn,
}: {
  providers: readonly IdentityProviderInfo[];
  /** Why the user is looking at this screen mid-work (an expired session), if so. */
  notice?: string | null;
  /** A shell's identity — platform, version name, build number (ADR-029 §7) — if this is one. */
  install?: string | null;
  onSignIn: (idp: string) => void;
}) {
  const t = useMessages();
  const g = t.grove;
  return (
    <section className="signin">
      <div className="signin-story">
        <p className="eyebrow">{g.signinEyebrow}</p>
        <h2>{g.signinHeadingStart}<br />{g.signinHeadingEnd}</h2>
        <p>{g.signinDescription}</p>
        <div className="signin-principles">
          <p>
            <strong>{g.planPrinciple}</strong> {g.planPrincipleDescription}
          </p>
          <p>
            <strong>{g.togetherPrinciple}</strong> {g.togetherPrincipleDescription}
          </p>
          <p>
            <strong>{g.movePrinciple}</strong> {g.movePrincipleDescription}
          </p>
        </div>
      </div>
      <div className="card signin-card">
        <p className="eyebrow">{g.welcome}</p>
        <h2>{t.signIn.heading}</h2>
        {notice !== null && (
          <p className="notice" role="status">{notice}</p>
        )}
        <p className="muted">{t.signIn.lead}</p>
        <div className="signin-actions">
          {providers.map((provider) => (
            <button
              className="button-secondary"
              key={provider.id}
              onClick={() => onSignIn(provider.id)}
            >
              {t.signIn.withProvider(provider.displayName)}
            </button>
          ))}
        </div>
        <p className="signin-account-note muted">{g.accountNote}</p>
        {install !== null && <footer aria-label={t.signIn.installLabel}>{t.signIn.installLine(install)}</footer>}
      </div>
    </section>
  );
}
